import React, { useState, useEffect } from 'react';
import { Plus, X, Check, Download, Upload, Trash2, Minus, Edit2 } from 'lucide-react';

function App() {
  const [activeTab, setActiveTab] = useState('habits');
  const [habits, setHabits] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [showAddHabit, setShowAddHabit] = useState(false);
  const [showAddTime, setShowAddTime] = useState(false);
  const [selectedHabit, setSelectedHabit] = useState(null);
  const [selectedTimeLog, setSelectedTimeLog] = useState(null);
  const [editingHabit, setEditingHabit] = useState(null);
  const [editingTimeLog, setEditingTimeLog] = useState(null);
  const [newHabit, setNewHabit] = useState({ name: '', interval: 1 });
  const [newTimeLog, setNewTimeLog] = useState({ name: '', hoursPerWeek: 0 });

  const neonColors = [
    '#FF006E', '#8338EC', '#3A86FF', '#06FFA5', '#FFBE0B',
    '#FB5607', '#FF006E', '#8338EC', '#06D6A0', '#FFD60A'
  ];

  const getRandomColor = () => neonColors[Math.floor(Math.random() * neonColors.length)];

  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDateString = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const getCurrentWeek = () => {
    const start = getWeekStart(new Date());
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const formatDate = (date) => {
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const formatWeekRange = (weekStart) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(checkWeekReset, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadData = () => {
    try {
      const saved = localStorage.getItem('habitTrackData');
      if (saved) {
        const data = JSON.parse(saved);
        setHabits(data.habits || []);
        setTimeLogs(data.timeLogs || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  useEffect(() => {
    if (habits.length > 0 || timeLogs.length > 0) {
      try {
        const data = { habits, timeLogs };
        localStorage.setItem('habitTrackData', JSON.stringify(data));
      } catch (error) {
        console.error('Error saving data:', error);
      }
    }
  }, [habits, timeLogs]);

  const checkWeekReset = () => {
    const currentWeek = getCurrentWeek().getTime();
    setTimeLogs(prev => {
      const updated = prev.map(log => {
        if (log.currentWeekStart !== currentWeek) {
          const history = log.history || [];
          if (log.currentWeekStart) {
            history.push({
              weekStart: log.currentWeekStart,
              hours: log.currentHours || 0,
              total: log.hoursPerWeek
            });
          }
          return {
            ...log,
            currentWeekStart: currentWeek,
            currentHours: 0,
            history
          };
        }
        return log;
      });
      return updated;
    });
  };

  const addHabit = () => {
    if (!newHabit.name.trim()) return;
    const habit = {
      id: Date.now(),
      name: newHabit.name,
      interval: parseInt(newHabit.interval) || 1,
      color: getRandomColor(),
      completions: []
    };
    setHabits([...habits, habit]);
    setNewHabit({ name: '', interval: 1 });
    setShowAddHabit(false);
  };

  const updateHabit = () => {
    if (!editingHabit || !editingHabit.name.trim()) return;
    setHabits(habits.map(h => 
      h.id === editingHabit.id 
        ? { ...h, name: editingHabit.name, interval: parseInt(editingHabit.interval) || 1 }
        : h
    ));
    setEditingHabit(null);
  };

  const deleteHabit = (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this habit?')) return;
    setHabits(habits.filter(h => h.id !== id));
  };

  const toggleHabitCompletion = (habitId, date) => {
    setHabits(habits.map(habit => {
      if (habit.id === habitId) {
        const dateStr = getLocalDateString(date);
        const existing = habit.completions.find(c => c.date === dateStr);
        
        if (existing) {
          return {
            ...habit,
            completions: habit.completions.filter(c => c.date !== dateStr)
          };
        } else {
          return {
            ...habit,
            completions: [...habit.completions, { date: dateStr, interval: habit.interval }]
          };
        }
      }
      return habit;
    }));
  };

  const isDateCompleted = (habit, date) => {
    const dateStr = getLocalDateString(date);
    return habit.completions.some(completion => {
      const completionDate = parseLocalDateString(completion.date);
      const daysDiff = Math.floor((date - completionDate) / (1000 * 60 * 60 * 24));
      return daysDiff >= 0 && daysDiff < completion.interval;
    });
  };

  const isTodayCompleted = (habit) => {
    return isDateCompleted(habit, new Date());
  };

  const addTimeLog = () => {
    if (!newTimeLog.name.trim()) return;
    const log = {
      id: Date.now(),
      name: newTimeLog.name,
      hoursPerWeek: parseInt(newTimeLog.hoursPerWeek) || 0,
      currentHours: 0,
      currentWeekStart: getCurrentWeek().getTime(),
      color: getRandomColor(),
      history: []
    };
    setTimeLogs([...timeLogs, log]);
    setNewTimeLog({ name: '', hoursPerWeek: 0 });
    setShowAddTime(false);
  };

  const updateTimeLog = () => {
    if (!editingTimeLog || !editingTimeLog.name.trim()) return;
    setTimeLogs(timeLogs.map(t => 
      t.id === editingTimeLog.id 
        ? { ...t, name: editingTimeLog.name, hoursPerWeek: parseInt(editingTimeLog.hoursPerWeek) || 0 }
        : t
    ));
    setEditingTimeLog(null);
  };

  const deleteTimeLog = (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    setTimeLogs(timeLogs.filter(t => t.id !== id));
  };

  const updateTimeLogHours = (id, delta) => {
    setTimeLogs(timeLogs.map(log => {
      if (log.id === id) {
        const newHours = Math.max(0, Math.min(log.hoursPerWeek, log.currentHours + delta));
        return { ...log, currentHours: newHours };
      }
      return log;
    }));
  };

  const exportData = () => {
    const data = {
      habits,
      timeLogs,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
    const text = JSON.stringify(data, null, 2);
    
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `habittrack-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert('Backup downloaded!');
  };

  const importData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          setHabits(data.habits || []);
          setTimeLogs(data.timeLogs || []);
          alert('Data restored successfully!');
        } catch (error) {
          alert('Invalid backup file!');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const getLast5Weeks = () => {
    const weeks = [];
    const today = new Date();
    for (let i = 4; i >= 0; i--) {
      const weekStart = getWeekStart(today);
      weekStart.setDate(weekStart.getDate() - (i * 7));
      weeks.push(weekStart);
    }
    return weeks;
  };

  const getTrackedWeeks = (habit) => {
    if (!habit.completions.length) return [];
    
    const weekMap = new Map();
    
    habit.completions.forEach(completion => {
      const date = new Date(completion.date);
      const weekStart = getWeekStart(date);
      const weekKey = weekStart.getTime();
      weekMap.set(weekKey, weekStart);
    });
    
    return Array.from(weekMap.values()).sort((a, b) => a - b);
  };

  const getTimeLogGridData = (log) => {
    const weeks = getLast5Weeks();
    const allWeeks = [...(log.history || []), {
      weekStart: log.currentWeekStart,
      hours: log.currentHours || 0,
      total: log.hoursPerWeek
    }];
    
    return weeks.map(weekStart => {
      const weekData = allWeeks.find(w => w.weekStart === weekStart.getTime());
      if (!weekData) return { weekStart, status: 'none' };
      
      const percentage = (weekData.hours / weekData.total) * 100;
      let status = 'red';
      if (percentage >= 100) status = 'green';
      else if (percentage > 0) status = 'orange';
      else status = 'red';
      
      return { weekStart, status, hours: weekData.hours, total: weekData.total };
    });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000', color: '#fff', paddingBottom: '80px' }}>
      <div style={{ maxWidth: '48rem', margin: '0 auto', padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>HabitTrack</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={exportData} style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#fff' }} title="Download Backup">
              <Download size={20} />
            </button>
            <button onClick={importData} style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#fff' }} title="Upload Backup">
              <Upload size={20} />
            </button>
          </div>
        </div>

        {activeTab === 'habits' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {habits.map(habit => {
              const todayCompleted = isTodayCompleted(habit);
              const isEditing = editingHabit && editingHabit.id === habit.id;
              const weeks = getLast5Weeks();
              
              return (
                <div key={habit.id} style={{ backgroundColor: '#111827', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #374151' }}>
                  {isEditing ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editingHabit.name}
                        onChange={(e) => setEditingHabit({ ...editingHabit, name: e.target.value })}
                        style={{ width: '100%', backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', marginBottom: '0.5rem', border: 'none', outline: 'none', color: '#fff' }}
                        placeholder="Habit name"
                      />
                      <input
                        type="number"
                        value={editingHabit.interval}
                        onChange={(e) => setEditingHabit({ ...editingHabit, interval: e.target.value })}
                        style={{ width: '100%', backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', marginBottom: '0.5rem', border: 'none', outline: 'none', color: '#fff' }}
                        placeholder="Interval (days)"
                        min="1"
                      />
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={updateHabit} style={{ flex: 1, borderRadius: '0.25rem', padding: '0.5rem', backgroundColor: '#9333EA', color: '#fff', border: 'none', cursor: 'pointer' }}>
                          Save
                        </button>
                        <button onClick={() => setEditingHabit(null)} style={{ flex: 1, backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', color: '#fff', border: 'none', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ cursor: 'pointer' }} onClick={() => setSelectedHabit(habit)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <h3 style={{ fontWeight: '600', color: habit.color }}>{habit.name}</h3>
                        <div style={{ display: 'flex', gap: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => toggleHabitCompletion(habit.id, new Date())}
                            style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer' }}
                          >
                            {todayCompleted ? (
                              <X size={18} color="#ef4444" />
                            ) : (
                              <Check size={18} color={habit.color} />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingHabit({ ...habit })}
                            style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={(e) => deleteHabit(e, habit.id)}
                            style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                        Every {habit.interval} day{habit.interval > 1 ? 's' : ''}
                      </div>
                      <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                        <div style={{ display: 'flex', gap: '0.25rem', minWidth: 'fit-content' }}>
                          {weeks.map((weekStart, weekIdx) => (
                            <div key={weekIdx} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                                const date = new Date(weekStart);
                                date.setDate(date.getDate() + dayOffset);
                                const isCompleted = isDateCompleted(habit, date);
                                const isToday = date.toDateString() === new Date().toDateString();
                                
                                return (
                                  <div
                                    key={dayOffset}
                                    style={{
                                      width: '2rem',
                                      height: '2rem',
                                      borderRadius: '0.25rem',
                                      border: `1px solid ${isToday ? habit.color : '#374151'}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.75rem',
                                      backgroundColor: isCompleted ? habit.color : 'transparent',
                                      boxShadow: isCompleted ? `0 0 10px ${habit.color}` : 'none',
                                      transition: 'all 0.3s'
                                    }}
                                  >
                                    {isToday && !isCompleted && '•'}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {showAddHabit ? (
              <div style={{ backgroundColor: '#111827', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #374151' }}>
                <input
                  type="text"
                  placeholder="Habit name"
                  value={newHabit.name}
                  onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })}
                  style={{ width: '100%', backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', marginBottom: '0.5rem', border: 'none', outline: 'none', color: '#fff' }}
                />
                <input
                  type="number"
                  placeholder="Interval (days)"
                  value={newHabit.interval}
                  onChange={(e) => setNewHabit({ ...newHabit, interval: e.target.value })}
                  style={{ width: '100%', backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', marginBottom: '0.5rem', border: 'none', outline: 'none', color: '#fff' }}
                  min="1"
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={addHabit} style={{ flex: 1, borderRadius: '0.25rem', padding: '0.5rem', backgroundColor: '#9333EA', color: '#fff', border: 'none', cursor: 'pointer' }}>
                    Add
                  </button>
                  <button onClick={() => setShowAddHabit(false)} style={{ flex: 1, backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', color: '#fff', border: 'none', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddHabit(true)}
                style={{ width: '100%', backgroundColor: '#111827', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#fff', cursor: 'pointer' }}
              >
                <Plus size={20} />
                <span>Add Habit</span>
              </button>
            )}
          </div>
        )}

        {activeTab === 'time' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {timeLogs.map(log => {
              const progress = (log.currentHours / log.hoursPerWeek) * 100;
              const gridData = getTimeLogGridData(log);
              const isEditing = editingTimeLog && editingTimeLog.id === log.id;
              
              return (
                <div key={log.id} style={{ backgroundColor: '#111827', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #374151' }}>
                  {isEditing ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editingTimeLog.name}
                        onChange={(e) => setEditingTimeLog({ ...editingTimeLog, name: e.target.value })}
                        style={{ width: '100%', backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', marginBottom: '0.5rem', border: 'none', outline: 'none', color: '#fff' }}
                        placeholder="Activity name"
                      />
                      <input
                        type="number"
                        value={editingTimeLog.hoursPerWeek}
                        onChange={(e) => setEditingTimeLog({ ...editingTimeLog, hoursPerWeek: e.target.value })}
                        style={{ width: '100%', backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', marginBottom: '0.5rem', border: 'none', outline: 'none', color: '#fff' }}
                        placeholder="Hours per week"
                        min="0"
                      />
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={updateTimeLog} style={{ flex: 1, borderRadius: '0.25rem', padding: '0.5rem', backgroundColor: '#9333EA', color: '#fff', border: 'none', cursor: 'pointer' }}>
                          Save
                        </button>
                        <button onClick={() => setEditingTimeLog(null)} style={{ flex: 1, backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', color: '#fff', border: 'none', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ cursor: 'pointer' }} onClick={() => setSelectedTimeLog(log)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <h3 style={{ fontWeight: '600', color: log.color }}>{log.name}</h3>
                        <div style={{ display: 'flex', gap: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setEditingTimeLog({ ...log })}
                            style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={(e) => deleteTimeLog(e, log.id)}
                            style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                        {log.currentHours} / {log.hoursPerWeek} hours
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => updateTimeLogHours(log.id, -1)}
                          style={{ width: '2rem', height: '2rem', borderRadius: '50%', backgroundColor: '#1f2937', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}
                        >
                          <Minus size={16} />
                        </button>
                        
                        <div style={{ flex: 1, height: '0.5rem', backgroundColor: '#1f2937', borderRadius: '9999px', overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              width: `${Math.min(100, progress)}%`,
                              backgroundColor: log.color,
                              boxShadow: `0 0 10px ${log.color}`,
                              transition: 'all 0.3s'
                            }}
                          />
                        </div>
                        
                        <button
                          onClick={() => updateTimeLogHours(log.id, 1)}
                          style={{ width: '2rem', height: '2rem', borderRadius: '50%', backgroundColor: '#1f2937', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                        {gridData.map((week, idx) => {
                          let bgColor = '#374151';
                          if (week.status === 'green') bgColor = '#10B981';
                          else if (week.status === 'orange') bgColor = '#F59E0B';
                          else if (week.status === 'red') bgColor = '#EF4444';
                          
                          return (
                            <div
                              key={idx}
                              style={{ width: '3rem', height: '2rem', borderRadius: '0.25rem', backgroundColor: bgColor }}
                              title={week.status !== 'none' ? `${week.hours}/${week.total}h` : 'No data'}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {showAddTime ? (
              <div style={{ backgroundColor: '#111827', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #374151' }}>
                <input
                  type="text"
                  placeholder="Activity name"
                  value={newTimeLog.name}
                  onChange={(e) => setNewTimeLog({ ...newTimeLog, name: e.target.value })}
                  style={{ width: '100%', backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', marginBottom: '0.5rem', border: 'none', outline: 'none', color: '#fff' }}
                />
                <input
                  type="number"
                  placeholder="Hours per week"
                  value={newTimeLog.hoursPerWeek}
                  onChange={(e) => setNewTimeLog({ ...newTimeLog, hoursPerWeek: e.target.value })}
                  style={{ width: '100%', backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', marginBottom: '0.5rem', border: 'none', outline: 'none', color: '#fff' }}
                  min="0"
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={addTimeLog} style={{ flex: 1, borderRadius: '0.25rem', padding: '0.5rem', backgroundColor: '#9333EA', color: '#fff', border: 'none', cursor: 'pointer' }}>
                    Add
                  </button>
                  <button onClick={() => setShowAddTime(false)} style={{ flex: 1, backgroundColor: '#1f2937', borderRadius: '0.25rem', padding: '0.5rem', color: '#fff', border: 'none', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddTime(true)}
                style={{ width: '100%', backgroundColor: '#111827', borderRadius: '0.5rem', padding: '1rem', border: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#fff', cursor: 'pointer' }}
              >
                <Plus size={20} />
                <span>Add Activity</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#111827', borderTop: '1px solid #374151' }}>
        <div style={{ maxWidth: '48rem', margin: '0 auto', display: 'flex' }}>
          <button
            onClick={() => setActiveTab('habits')}
            style={{
              flex: 1,
              padding: '1rem',
              background: 'transparent',
              border: 'none',
              borderTop: activeTab === 'habits' ? '2px solid #A78BFA' : 'none',
              color: activeTab === 'habits' ? '#A78BFA' : '#9ca3af',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Habits
          </button>
          <button
            onClick={() => setActiveTab('time')}
            style={{
              flex: 1,
              padding: '1rem',
              background: 'transparent',
              border: 'none',
              borderTop: activeTab === 'time' ? '2px solid #A78BFA' : 'none',
              color: activeTab === 'time' ? '#A78BFA' : '#9ca3af',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Time Logger
          </button>
        </div>
      </div>

      {selectedHabit && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div style={{ backgroundColor: '#111827', borderRadius: '0.5rem', padding: '1.5rem', maxWidth: '28rem', width: '100%', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: selectedHabit.color }}>{selectedHabit.name}</h3>
              <button onClick={() => setSelectedHabit(null)} style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            {getTrackedWeeks(selectedHabit).length === 0 ? (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>No tracking data yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {getTrackedWeeks(selectedHabit).map((weekStart, idx) => (
                  <div key={idx} style={{ borderBottom: '1px solid #1f2937', paddingBottom: '0.75rem' }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      Week {formatWeekRange(weekStart)}
                    </div>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, dayIdx) => {
                      const date = new Date(weekStart);
                      date.setDate(date.getDate() + dayIdx);
                      const isCompleted = isDateCompleted(selectedHabit, date);
                      
                      return (
                        <div key={dayIdx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                          <span style={{ color: '#9ca3af' }}>{day} ({formatDate(date)})</span>
                          <span style={{ color: isCompleted ? selectedHabit.color : '#9ca3af' }}>
                            {isCompleted ? 'Done' : 'Not Done'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedTimeLog && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div style={{ backgroundColor: '#111827', borderRadius: '0.5rem', padding: '1.5rem', maxWidth: '28rem', width: '100%', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: selectedTimeLog.color }}>{selectedTimeLog.name}</h3>
              <button onClick={() => setSelectedTimeLog(null)} style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[...(selectedTimeLog.history || []), {
                weekStart: selectedTimeLog.currentWeekStart,
                hours: selectedTimeLog.currentHours || 0,
                total: selectedTimeLog.hoursPerWeek
              }].filter(w => w.weekStart).reverse().map((week, idx) => {
                const weekStart = new Date(week.weekStart);
                return (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #1f2937' }}>
                    <span style={{ color: '#9ca3af' }}>Week {formatWeekRange(weekStart)}</span>
                    <span style={{ color: selectedTimeLog.color }}>
                      {week.hours}/{week.total} hours
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;